/**
 * Auto-save orchestrator for the brief editor.
 *
 * Observes `changeCounter` (a monotonic integer owned by `useBriefEditorForm`)
 * and, after a debounce window, calls `save(getPayload())` — provided the
 * form is valid AND no save is already in flight.
 *
 * Why a counter and not the payload:
 *   - The payload is a fresh object reference on every render (defensive
 *     freeze + map). Debouncing on the payload would trip on every keystroke
 *     even for no-op edits, defeating the point.
 *   - The integer counter increments exactly once per title/value mutation
 *     (see `useBriefEditorForm`) and is structurally cheap to compare.
 *
 * Section edits do NOT increment the counter (handled in
 * `useBriefEditorForm`), so auto-save never fires for section-only edits.
 * Sections persist only through the manual Save button, which calls the
 * mutation directly with `getFullSubmitPayload()`.
 *
 * The lone React effect hook is encapsulated inside
 * `useDebouncedAction`. This module imports the helper rather than the
 * effect primitive directly, so the no-direct-effect rule stays clean at
 * the call site.
 */
import { useCallback } from 'react';

import { useDebouncedAction } from '@/shared/hooks/useDebouncedAction/useDebouncedAction';

export interface UseBriefAutoSaveParams<TPayload> {
  /** Monotonic counter incremented on each title/value mutation. */
  changeCounter: number;
  /** Form validity gate — auto-save is suppressed when false. */
  isValid: boolean;
  /** Mutation in-flight gate — auto-save is suppressed to avoid overlap. */
  isPending: boolean;
  /** Builds the payload at dispatch time (always reads the latest state). */
  getPayload: () => TPayload;
  /** Consumer-supplied dispatcher (typically wraps `mutation.mutate`). */
  save: (payload: TPayload) => void;
  /** Trailing-edge debounce window. Defaults to 1000ms (per AC-1). */
  debounceMs?: number;
}

const DEFAULT_DEBOUNCE_MS = 1_000;

export const useBriefAutoSave = <TPayload>({
  changeCounter,
  isValid,
  isPending,
  getPayload,
  save,
  debounceMs = DEFAULT_DEBOUNCE_MS,
}: UseBriefAutoSaveParams<TPayload>): void => {
  const handleDebounceFire = useCallback(() => {
    if (!isValid || isPending) return;
    save(getPayload());
  }, [isValid, isPending, save, getPayload]);

  useDebouncedAction(changeCounter, handleDebounceFire, debounceMs);
};
